import { all, call, put, select, takeLeading } from 'redux-saga/effects';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toastr from 'toastr';
import i18n from '../../i18n';
import types from './actionTypes';
import * as actions from './actions';

const selectSimulation = (state) => state.AiSimulation;

const callSimulator = (body) => {
  const functions = getFunctions();
  const simulate = httpsCallable(
    functions,
    'simulateAiConversation-simulateAiConversation',
  );
  return call(simulate, body);
};

function* startSimulationSaga({ agentId, acquisitionOrderId, lead, mode }) {
  try {
    const { data } = yield callSimulator({
      action: 'start',
      agentId,
      acquisitionOrderId,
      session: null,
      payload: { lead, mode },
    });
    if (data.error) throw new Error(data.error.message);
    yield put(actions.startSimulationSuccess(data));
  } catch (error) {
    yield put(actions.startSimulationFailure(error));
    toastr.error(error.message, i18n.t('Error'));
  }
}

// A turn needs the session the browser is holding, so every continuation
// reads it from the store rather than taking it from the component: the
// component must never need to know what is inside it.
function* continueSimulation(action, payload, success) {
  const { target, session } = yield select(selectSimulation);
  if (!target || !session) throw new Error(i18n.t('No simulation in progress'));
  const { data } = yield callSimulator({
    action,
    agentId: target.agentId,
    acquisitionOrderId: target.acquisitionOrderId,
    session,
    payload,
  });
  if (data.error) throw new Error(data.error.message);
  yield put(success(data));
}

function* replySimulationSaga({ text }) {
  try {
    yield* continueSimulation(
      'reply',
      { text },
      actions.replySimulationSuccess,
    );
  } catch (error) {
    yield put(actions.replySimulationFailure(error));
    toastr.error(error.message, i18n.t('Error'));
  }
}

function* advanceSimulationSaga({ minutes }) {
  try {
    yield* continueSimulation(
      'advanceTime',
      { minutes },
      actions.advanceSimulationSuccess,
    );
  } catch (error) {
    yield put(actions.advanceSimulationFailure(error));
    toastr.error(error.message, i18n.t('Error'));
  }
}

// takeLeading, not takeLatest: a second click while a turn is in flight is
// ignored, not raced. Two turns racing would both build on the same session
// and one would silently win, losing the other's writes.
function* aiSimulationSaga() {
  yield all([
    takeLeading(types.START_AI_SIMULATION.REQUEST, startSimulationSaga),
    takeLeading(types.REPLY_AI_SIMULATION.REQUEST, replySimulationSaga),
    takeLeading(types.ADVANCE_AI_SIMULATION.REQUEST, advanceSimulationSaga),
  ]);
}

export { startSimulationSaga, replySimulationSaga, advanceSimulationSaga };

export default aiSimulationSaga;
